import { decimal, label } from '@entities/ksp/format';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import { Button } from '@shared/ui/Button';
import { KspSummary } from '@widgets/ksp-workspace/Summary';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line as ChartLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, Muted, Notice, Row, Small } from '../styles';
import { PagedTable } from '../components/CollectionTable';

export interface PlanFactTabProps {
  workspace: KspWorkspace;
  permission: (action?: KspAction) => boolean;
  createDocument: (kind: string) => void;
}

export function PlanFactTab({
  workspace: d,
  permission,
  createDocument,
}: PlanFactTabProps) {
  return (
    <>
      <KspSummary data={d.summary} />
      <Card>
        <h3>Выполнение по версиям объёмов</h3>
        <Muted>
          Базовый график и текущий утверждённый объём сохраняют отдельные знаменатели.
          Несогласованный объём новой версии не меняет принятый показатель.
        </Muted>
        <PagedTable
          rows={d.activities}
          columns={[
            { name: 'Работа', value: (activity) => `${activity.code} · ${activity.name}` },
            {
              name: 'Объём базового графика',
              value: (activity) =>
                `${decimal(activity.baseline_quantity)} ${activity.baseline_unit ?? ''}`,
            },
            {
              name: 'Факт к базовому объёму',
              value: (activity) => (
                <>
                  {decimal(activity.baseline_fact_pct, ' %')}
                  {activity.baseline_reason && (
                    <Small>{label(activity.baseline_reason)}</Small>
                  )}
                </>
              ),
            },
            {
              name: 'Текущий утверждённый объём',
              value: (activity) =>
                `${decimal(activity.current_quantity)} ${activity.current_unit ?? ''}`,
            },
            {
              name: 'Факт к текущему объёму',
              value: (activity) => (
                <>
                  {decimal(activity.current_fact_pct, ' %')}
                  {activity.current_reason && <Small>{label(activity.current_reason)}</Small>}
                </>
              ),
            },
          ]}
        />
      </Card>
      <Card>
        <Row>
          <h3>Накопительные кривые</h3>
          <Button
            disabled={!permission() || !d.summary}
            onClick={() => createDocument('deviation_act')}
          >
            Акт фиксации отклонений
          </Button>
        </Row>
        {d.summary?.curves?.map((curve, i) => (
          <Card key={i}>
            <Muted>
              {label(curve.method)} · {curve.unit} · {label(d.summary?.coverage ?? 'unknown')}
            </Muted>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart
                data={curve.points.map((point) => ({
                  date: point.date,
                  plan: point.baseline == null ? null : Number(point.baseline),
                  actual: point.actual == null ? null : Number(point.actual),
                  forecast: point.forecast == null ? null : Number(point.forecast),
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Area
                  dataKey="plan"
                  name="Baseline"
                  fill="#999999"
                  stroke="#777777"
                  fillOpacity={0.15}
                  connectNulls={false}
                />
                <ChartLine
                  dataKey="actual"
                  name="Принятый факт"
                  stroke="#298856"
                  dot={false}
                  connectNulls={false}
                />
                <ChartLine
                  dataKey="forecast"
                  name="Прогноз"
                  stroke="#ba3949"
                  strokeDasharray="5 3"
                  dot={false}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
            <Small>
              Точность показателя сохраняется в таблицах и выгрузках; линия — визуальное
              представление. Пустые окна не подменяются нулём.
            </Small>
          </Card>
        ))}
        {!d.summary?.curves?.length && (
          <Notice>
            Кривые пока не рассчитаны или отсутствует подтверждённая методика. Проверьте веса,
            baseline и покрытие факта.
          </Notice>
        )}
      </Card>
    </>
  );
}
